import { Infra3DSDKError } from "../error/Infra3DSDKError";
import { GeoCoords } from "../geo/GeoCoords";
/**
 * @class GeometryProviderBase
 *
 * @classdesc Base class to extend if implementing a geometry
 * provider class.
 *
 * @example
 * ```
 * class MyGeometryProvider extends Mapillary.API.GeometryProviderBase {
 *      ...
 * }
 * ```
 */
export class GeometryProviderBase {
    /**
     * Create a new geometry provider base instance.
     *
     * @ignore @param {GeoCoords} [geoCoords] - Optional geo coords instance.
     */
    constructor(geoCoords) {
        this._geoCoords = geoCoords != null ? geoCoords : new GeoCoords();
    }
    /**
     * Convert a geodetic bounding box to the the minimum set
     * of cell ids containing the bounding box.
     *
     * @description The bounding box needs
     * to be sufficiently small to be contained in an area with the size
     * of maximally four tiles. Up to nine adjacent tiles may be returned.
     *
     * @param {ILatLon} sw - South west corner of bounding box.
     * @param {ILatLon} ne - North east corner of bounding box.
     *
     * @returns {Array<string>} Array of cell ids.
     */
    bboxToCellIds(sw, ne) {
        throw new Infra3DSDKError("Not implemented");
    }
    /**
     * Get the corners of a cell.
     *
     * @param {string} cellId - Id of cell.
     * @returns {ICellCorners} Cell corners struct.
     */
    getCorners(cellId) {
        throw new Infra3DSDKError("Not implemented");
    }
    /**
     * Get the neighbors of a cell.
     *
     * @param {ILatLon} sw - South west corner of the bounding box.
     * @param {ILatLon} ne - North east corner of the bounding box.
     * @returns {ICellCorners} Cell corners struct.
     */
    getNeighbors(cellId) {
        throw new Infra3DSDKError("Not implemented");
    }
    /**
     * Convert geodetic coordinates to a cell id.
     *
     * @param {ILatLon} latlon - Latitude and longitude to convert.
     * @returns {string} Cell id for the latitude, longitude.
     */
    latLonToCellId(latLon, relativeLevel) {
        throw new Infra3DSDKError("Not implemented");
    }
    /**
     * Convert a geodetic square to cell ids.
     *
     * The square is specified as a latitude, longitude
     * and a threshold from the position using Manhattan distance.
     *
     * @param {ILatLon} latlon - Latitude and longitude.
     * @param {number} threshold - Threshold of the conversion in meters.
     *
     * @returns {Array<string>} Array of cell ids reachable within
     * the threshold.
     */
    latLonToCellIds(latLon, threshold, relativeLevel) {
        throw new Infra3DSDKError("Not implemented");
    }
    _bboxSquareToCellIds(sw, ne) {
        if (ne.lat <= sw.lat || ne.lon <= sw.lon) {
            throw new Infra3DSDKError("North east needs to be top right of south west");
        }
        const centerLat = (sw.lat + ne.lat) / 2;
        const centerLon = (sw.lon + ne.lon) / 2;
        const enu = this._geoCoords.geodeticToEnu(ne.lat, ne.lon, 0, centerLat, centerLon, 0);
        const threshold = Math.max(enu[0], enu[1]);
        //fixme: add more general solution
        const relativeLevel = 0;
        /* if (threshold>25){
            relativeLevel = -1;
        } */
        return this.latLonToCellIds({ lat: centerLat, lon: centerLon }, threshold, relativeLevel);
    }
}
//# sourceMappingURL=GeometryProviderBase.js.map